import React, { FunctionComponent, memo, RefObject } from 'react'
import Grid from '@sport1/react-elements/Grid'
import PlatformHelper from '@sport1/react-elements/PlatformHelper'
import { ChannelLogoTypes } from '../../Theme/Logos/LogoInterfaces'
import { getLogo } from '../../utils/LogoHelper'
import { TestID } from '../../utils/PropTypesDefinitions'
import { ImageLabel } from '../../BaseComponents/Image/Components'
import BigButton from '../../BaseComponents/Button/Standard/Big'
import BasicText, { WebHeadlineType } from '../../Theme/Fonts/BasicText'
import Tonal from '../../BaseComponents/Tonal'
import { LabelProps } from '../../BaseComponents/Labels/Types'
import SmallButton from '../../BaseComponents/Button/Standard/Small'
import Image from '../../BaseComponents/Image'

export type TVStreamTeaserProps = WebHeadlineType &
    TestID &
    LabelProps & {
        /** the type of the logo (sport1, esport, sport1plus) */
        logoType?: ChannelLogoTypes
        /** the maxHeight of the component */
        maxHeight?: number
        /** the text of the headline */
        headlineText: string
        /** the small description  below the main headline */
        subHeadlineText?: string
        /** the text of the button */
        buttonText?: string
        /** the icon of the button */
        buttonIcon?: 'play-active'
        /** the image of the teaser */
        mainImageUrl: string
        /** the seo info for mainImageUrl */
        mainImageAlt?: string
        /** the webHeadlineType for the subHeadline */
        subWebHeadlineType?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
        /** callback when the user pressed the play button
         *
         * @TJS-type function
         * */
        onPress?: () => void
        /** setting if element is a webTBRegionItem */
        webTBRegionItem?: string
        /**
         * property for ref forwarding
         * @TJS-type React.RefObject<Element>
         * */
        innerRef?: RefObject<Element>
    }

const TVStreamTeaser: FunctionComponent<TVStreamTeaserProps> = ({
    logoType = 'sport1',
    maxHeight,
    labels,
    buttonIcon,
    headlineText,
    subHeadlineText,
    buttonText,
    onPress,
    mainImageUrl,
    mainImageAlt,
    webHeadlineType,
    subWebHeadlineType,
    innerRef,
    testID,
    webTBRegionItem,
}) => {
    return (
        <Grid ref={innerRef} noExpand maxHeight={maxHeight} testID={testID} position="relative">
            <Tonal opacity={0.5} borderRadius={[0, 0, 0, 4]} backgroundColor="BLACK" />
            <Image imageUrl={mainImageUrl} alt={mainImageAlt} size="L" />
            {labels ? <ImageLabel labels={labels} margin={5} /> : null}
            <Grid position="absolute" margin={5} right={0} zIndex={5} top={0}>
                {getLogo(logoType, 80)}
            </Grid>
            <Grid zIndex={5} position="absolute" margin={5} left={0} right={0} bottom={0} noExpand>
                <BasicText
                    fontSize={['FS18', 'FS22', 'FS30']}
                    lineHeight={['LH22', 'LH24', 'LH36']}
                    bold
                    color="white"
                    webHeadlineType={webHeadlineType}
                    numberOfLines={PlatformHelper.getDimensions().width >= 768 ? 2 : 3}
                    className={webTBRegionItem === '' ? 's1-taboola-title' : undefined}
                >
                    {headlineText}
                </BasicText>
                {subHeadlineText ? (
                    <Grid
                        marginTop={[1, 3, 3]}
                        marginBottom={buttonText ? [4, 5, 5] : [4, 5, 0]}
                        noExpand
                    >
                        <BasicText
                            fontSize={['FS14', 'FS16', 'FS18']}
                            lineHeight={['LH16', 'LH16', 'LH22']}
                            color="white"
                            numberOfLines={1}
                            webHeadlineType={subWebHeadlineType}
                        >
                            {subHeadlineText}
                        </BasicText>
                    </Grid>
                ) : (
                    <Grid noExpand marginTop={[4, 4, 5]} />
                )}
                {buttonText ? (
                    <>
                        <Grid display={['none', 'none', 'flex']}>
                            <BigButton
                                backgroundColor="WHITE"
                                onPress={onPress}
                                iconName={buttonIcon}
                                testID={`button-${testID}`}
                            >
                                {buttonText}
                            </BigButton>
                        </Grid>
                        <Grid display={['flex', 'flex', 'none']}>
                            <SmallButton
                                backgroundColor="WHITE"
                                onPress={onPress}
                                testID={`button-${testID}`}
                                iconName={buttonIcon}
                            >
                                {buttonText}
                            </SmallButton>
                        </Grid>
                    </>
                ) : null}
            </Grid>
        </Grid>
    )
}

export default memo(TVStreamTeaser)
